import React from 'react';
import { useDispatch } from 'react-redux';
import { resetCookieConsent } from '../store/slices/cookieConsentSlice';

/**
 * CookiePolicyPage — public, simple, brand-compliant.
 *
 * Container width: --container-narrow (720px) for prose readability.
 * Cormorant H1, Jost body. No Markdown lists — this page uses prose
 * paragraphs and minimal structure to honor the design system.
 *
 * The "Manage preferences" button at the bottom resets the consent slice
 * and re-shows the banner.
 */
export default function CookiePolicyPage() {
  const dispatch = useDispatch();

  return (
    <main className="min-h-screen bg-cream-100 pt-24 md:pt-32 pb-24">
      <div className="max-w-2xl mx-auto px-6">
        <p className="section-label mb-3">Cookie policy</p>
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-tight tracking-tight mb-8">
          How we use cookies
        </h1>

        <div className="space-y-6 font-sans text-base text-charcoal leading-relaxed">
          <p>
            Contractor Robb Flooring uses a small number of cookies to keep the
            site working and to help us improve it. This page explains what
            those cookies do and how to control them.
          </p>

          <h2 className="font-serif text-2xl text-charcoal pt-4">Essential cookies</h2>
          <p>
            These cookies are required for the site to function. They sign you
            in to your account, keep your quote cart and shop cart separate, and
            protect form submissions from cross-site request forgery. We can't
            turn these off — without them you wouldn't be able to use the site.
          </p>
          <p className="font-sans text-sm text-warm-stone">
            Examples: <span className="font-medium text-charcoal">cr_token</span> (your
            sign-in session), <span className="font-medium text-charcoal">cr_session</span>
            {' '}(used briefly during Google sign-in),
            and <span className="font-medium text-charcoal">cr_blue_cart</span> /
            {' '}<span className="font-medium text-charcoal">cr_green_cart</span>
            {' '}(your quote and shop carts).
          </p>

          <h2 className="font-serif text-2xl text-charcoal pt-4">Analytics cookies</h2>
          <p>
            Optional. If allowed, these help us understand which pages and
            products are most useful so we can improve the experience. We do
            not sell or share this data.
          </p>

          <h2 className="font-serif text-2xl text-charcoal pt-4">Marketing cookies</h2>
          <p>
            Optional. If allowed, these help measure the effectiveness of our
            marketing. We do not currently run third-party ad pixels, but this
            category exists so you have a clear opt-in if we add them later.
          </p>

          <h2 className="font-serif text-2xl text-charcoal pt-4">Managing your choices</h2>
          <p>
            You can change your preferences any time. Use the button below to
            re-open the cookie banner and update your choices. Your decision is
            stored on your device only.
          </p>

          <button
            type="button"
            onClick={() => dispatch(resetCookieConsent())}
            className="bg-gold text-charcoal font-sans text-xs tracking-widest uppercase font-medium px-6 py-3 hover:bg-charcoal hover:text-white transition-colors mt-2"
          >
            Manage cookie preferences
          </button>

          <p className="font-sans text-xs text-warm-stone pt-8 border-t border-gray-200">
            Love3 Products, Inc. · 900 Faulk Road Suite 201, Wilmington, DE 19803
          </p>
        </div>
      </div>
    </main>
  );
}
